import { getCookie, removeCookie } from "../utility/cookie";

const apiUrl = process.env.REACT_APP_API_URL || "";
const apiOrigin = apiUrl ? new URL(apiUrl, window.location.origin).origin : "";
let validUntil = 0;
let sessionOwner = "";
let sessionToken = "";
let pending: Promise<void> | undefined;
let revoking = false;
let logoutPending: Promise<void> | undefined;

export class FileSessionError extends Error {
  response: { status: number };
  constructor(status: number) {
    super(`File authentication failed (${status})`);
    this.response = { status };
  }
}

const sessionRequest = async (
  url: string,
  init: RequestInit,
): Promise<Response> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10000);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
};

export const isTrustedApiRequest = (value = "", base = apiUrl): boolean => {
  if (!apiOrigin) return false;
  try {
    const url = new URL(value, new URL(base, window.location.origin));
    return url.origin === apiOrigin && !url.username && !url.password;
  } catch {
    return false;
  }
};

// Do not restore browser credentials from an API response that arrives after
// logout, or from a request belonging to the previous account/client.
export const shouldPersistAuthResponse = (
  requestClient: unknown,
  signIn = false,
  requestUid?: unknown,
): boolean => {
  if (revoking) return false;
  return (
    signIn ||
    Boolean(
      getCookie("access_token") &&
        typeof requestClient === "string" &&
        requestClient === localStorage.getItem("client") &&
        requestUid === localStorage.getItem("uid"),
    )
  );
};

export const isProtectedFileUrl = (value: string): boolean => {
  if (!apiOrigin) return false;
  try {
    const url = new URL(value, window.location.origin);
    return (
      url.origin === apiOrigin && /^\/(uploads|export)(\/|$)/.test(url.pathname)
    );
  } catch {
    return false;
  }
};

// This endpoint cannot authenticate update APIs. No token is added to a URL.
export const ensureFileSession = (force = false): Promise<void> => {
  if (revoking) return Promise.reject(new FileSessionError(401));
  const token = getCookie("access_token");
  const client = localStorage.getItem("client");
  const uid = localStorage.getItem("uid");
  if (!token || !client || !uid || !apiOrigin) {
    validUntil = 0;
    return Promise.resolve();
  }
  const owner = `${uid}:${client}`;
  if (
    !force &&
    owner === sessionOwner &&
    token === sessionToken &&
    validUntil > performance.now() + 60000
  ) {
    return Promise.resolve();
  }
  if (pending) {
    if (owner === sessionOwner && token === sessionToken) return pending;
    return pending.catch(() => undefined).then(() => ensureFileSession(force));
  }
  validUntil = 0;
  sessionOwner = owner;
  sessionToken = token;
  const attempt = sessionRequest(`${apiOrigin}/api/v1/file_session`, {
    method: "POST",
    credentials: "include",
    headers: { "access-token": token, client, uid },
  }).then(async (response) => {
    if (!response.ok) throw new FileSessionError(response.status);
    const result: { expires_at?: unknown; expires_in?: unknown } =
      await response.json();
    if (
      typeof result.expires_in !== "number" ||
      result.expires_in <= 0 ||
      result.expires_in > 86400
    ) {
      throw new Error("File authentication returned an invalid expiry");
    }
    if (sessionOwner === owner)
      validUntil = performance.now() + result.expires_in * 1000;
  });
  pending = attempt;
  void attempt
    .finally(() => {
      if (pending === attempt) pending = undefined;
    })
    .catch(() => undefined);
  return attempt;
};

const performRevokeFileSession = async (): Promise<void> => {
  const ownerClient = localStorage.getItem("client");
  const ownerUid = localStorage.getItem("uid");
  revoking = true;
  validUntil = 0;
  let serverConfirmed = false;
  try {
    await pending?.catch(() => undefined);
    const token = getCookie("access_token");
    const client = localStorage.getItem("client");
    const uid = localStorage.getItem("uid");
    if (token && client && uid && apiOrigin) {
      const response = await sessionRequest(
        `${apiOrigin}/api/v1/auth/sign_out`,
        {
          method: "DELETE",
          headers: { "access-token": token, client, uid },
        },
      );
      if (!response.ok && response.status !== 401)
        throw new FileSessionError(response.status);
    }
    serverConfirmed = true;
  } finally {
    // A successful server logout already invalidates the cookie. Cleanup is
    // still attempted when the server request fails so the browser drops it.
    if (apiOrigin) {
      await sessionRequest(`${apiOrigin}/api/v1/file_session`, {
        method: "DELETE",
        credentials: "include",
      }).catch(() => undefined);
    }
    // Clear credentials before releasing the logout guard: a queued API
    // response must never restore a token or start another cookie request.
    if (
      serverConfirmed &&
      localStorage.getItem("client") === ownerClient &&
      localStorage.getItem("uid") === ownerUid
    ) {
      removeCookie("access_token");
      localStorage.removeItem("client");
      localStorage.removeItem("uid");
      localStorage.removeItem("expiry");
    }
    sessionOwner = "";
    sessionToken = "";
    revoking = false;
  }
};

export const revokeFileSession = (): Promise<void> => {
  if (logoutPending) return logoutPending;
  const attempt = performRevokeFileSession();
  logoutPending = attempt;
  void attempt
    .finally(() => {
      if (logoutPending === attempt) logoutPending = undefined;
    })
    .catch(() => undefined);
  return attempt;
};

export const fileRecoveryTarget = (search: string): string | null => {
  const path = new URLSearchParams(search).get("path");
  if (
    !path ||
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    !apiOrigin
  )
    return null;
  if (!isProtectedFileUrl(new URL(path, apiOrigin).href)) return null;
  return `/file-access?path=${encodeURIComponent(path)}`;
};

export const fetchProtectedFile = async (value: string): Promise<Response> => {
  if (!isProtectedFileUrl(value)) return fetch(value);
  await ensureFileSession();
  return fetch(value, { credentials: "include" });
};

// Also refresh after an idle/background tab resumes before the next interaction.
export const startFileSessionRefresh = (): (() => void) => {
  const refresh = () => {
    // Background audio/video can issue new Range requests. Keep its file cookie
    // alive too; cached validity limits actual renewal to at most daily when there are no API token rotations.
    void ensureFileSession().catch(() => {
      validUntil = 0;
    });
  };
  const timer = window.setInterval(refresh, 60000);
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", refresh);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("focus", refresh);
    document.removeEventListener("visibilitychange", refresh);
  };
};
